import React from 'react';
import { Progress, Typography } from 'antd';

const { Text } = Typography;

/**
 * One honest indicator instead of per-field nagging.
 *
 * Spec §8. Nagging each field trains people to dismiss the nag; a single count
 * with a reason gives them a target and an explanation. Never blocks anything —
 * the blocking fields are handled by ordinary Form validation.
 *
 * `suggestedRemaining` arrives as human LABELS, already resolved by the caller
 * from the server's policy. A column name must never reach a customer, and this
 * component deliberately has no label map of its own so it cannot invent one.
 */
export function CompletenessMeter({
  total,
  filled,
  suggestedRemaining,
}: {
  total: number;
  filled: number;
  suggestedRemaining: string[];
}): React.ReactElement | null {
  // The shop wants nothing extra at this stage — render nothing rather than an
  // empty "0 of 0" widget, which reads as a broken form.
  if (total <= 0) return null;

  const safeFilled = Math.max(0, Math.min(filled, total));
  const percent = Math.round((safeFilled / total) * 100);
  const done = safeFilled >= total;

  return (
    <div style={{ marginBottom: 12 }} data-testid="completeness-meter">
      <Progress
        percent={percent}
        size="small"
        showInfo={false}
        status={done ? 'success' : 'normal'}
      />
      <Text style={{ fontSize: 13 }}>
        {safeFilled} of {total} details provided
      </Text>
      {!done && suggestedRemaining.length > 0 && (
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Adding {suggestedRemaining.join(', ')} helps us quote accurately and get you booked
            faster.
          </Text>
        </div>
      )}
    </div>
  );
}

export default CompletenessMeter;
